import { isCypress } from '@/utils/constants';
import { useKsLocation } from '@/utils/ks-location.hooks';
import { datadogLogs as ddLogs } from '@datadog/browser-logs'; // https://docs.datadoghq.com/logs/log_collection/javascript/
import { datadogRum as ddRum, type RumErrorEvent } from '@datadog/browser-rum';
import { addReactError, reactPlugin } from '@datadog/browser-rum-react';
import type { LogMetadata } from '@knapsack/logger';
import { useEffect, useRef, type ErrorInfo as ReactErrorInfo } from 'react';

export { ddLogs, ddRum };

// declare module '@datadog/browser-core' {
//   interface Context extends LogMetadata {
//     foo: string;
//     // // Add your custom properties here
//     // userId?: string;
//     // siteId?: string;
//     // orgId?: string;
//     // userRole?: string;
//     // isInternalUser?: boolean;
//     // environment?: string;
//     // appVersion?: string;
//     // featureFlags?: Record<string, any>;
//     // Add more custom properties as needed
//   }
// }

export const commonDataDogConfig = {
  site: 'datadoghq.com',
  service: 'app-ui',
  env: process.env.NEXT_PUBLIC_ENV || 'development',
  version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
  // Options that must have a matching configuration with the RUM and Logs SDKs:
  sessionPersistence: 'cookie',
  useSecureSessionCookie: true,
} as const;

export const logError = (error: Error, reactErrorInfo?: ReactErrorInfo) => {
  console.error(error);
  ddLogs.logger.error(error.message, { error }, error);
  if (reactErrorInfo) {
    addReactError(error, reactErrorInfo);
  } else {
    ddRum.addError(error);
  }
};

export const clearUser = () => {
  ddLogs.clearUser();
  ddRum.clearUser();
};

export const setUserId = (userId: string) => {
  ddLogs.setUser({ id: userId });
  ddRum.setUser({ id: userId });
};

export const setGlobalContext = (context: LogMetadata) =>
  ddLogs.setGlobalContext(context);

export const setGlobalContextProperty = <K extends keyof LogMetadata>(
  key: K,
  value: LogMetadata[K],
) => {
  ddLogs.setGlobalContextProperty(key, value);
  ddRum.setGlobalContextProperty(key, value);
};

export function useDatadogLogger() {
  const isInitialized = useRef(false);
  useEffect(() => {
    const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
    if (!clientToken) return;
    if (isCypress()) return;
    if (isInitialized.current) return;
    isInitialized.current = true;
    // @see: https://docs.datadoghq.com/logs/log_collection/javascript/#initialization-parameters
    ddLogs.init({
      ...commonDataDogConfig,
      clientToken,
      /**
       * Uses @see {ReportingObserver} to forward reports to Datadog
       * @see: https://developer.mozilla.org/en-US/docs/Web/API/Reporting_API
       * currently set to `[]` because we don't want to forward reports to Datadog as we *think* that the `Reporting-Endpoints` header set in `next.config.js` is enough
       */
      forwardReports: [],
      forwardErrorsToLogs: true,
      sessionSampleRate: 100, // percent
      telemetrySampleRate: 0, // percent of events to send to Datadog for their telemetry tracking
      // should give us info in login popup. might need to clear context in situations though
      storeContextsAcrossPages: true,

      /**
       * Return `false` to prevent the event from being sent to Datadog.
       * Can mutate the `event` object safely
       * Note that `event.http.url` is the URL of the HTTP request that triggered the event
       * Note that `event.view.url` is the URL of the current page
       * Note that `event.view.referrer` is the URL of the page that referred to the current page
       */
      beforeSend(event, _context) {
        switch (event.origin) {
          case 'network': {
            const u = new URL(event.http.url);
            if (
              ['api.segment.io', 'events.launchdarkly.com'].includes(u.host)
            ) {
              return false;
            }
            if (
              [
                '/api/v2/discovery',
                '/api/v1/save-data',
                '/_vercel/insights/view',
                '/_vercel/speed-insights/vitals',
              ].includes(u.pathname)
            ) {
              return false;
            }

            if (
              u.pathname.startsWith('/site/custom') &&
              u.pathname.includes('localhost')
            ) {
              // the are using a dev env and it's highly likely they stopped the KS App Client server
              return false;
            }
            // end `network` origin
            break;
          }
          case 'console': {
            // leaving this here for now
            break;
          }
        }

        if (event.http?.url) {
          const u = new URL(event.http.url);
          if (
            ['localhost', '127.0.0.1', 'app.launchdarkly.com'].includes(
              u.hostname,
            )
          ) {
            return false;
          }
          if (
            u.hostname === window.location.hostname &&
            u.pathname === '/monitoring'
          ) {
            return false;
          }
          if (u.searchParams.has('isOkToFail')) return false;
        }

        return true;
      },
    });
  }, []);
}

export function useDataDogRum() {
  const isInitialized = useRef(false);
  useEffect(() => {
    const rumEnabled = process.env.NEXT_PUBLIC_DATADOG_RUM_ENABLED === 'true';
    const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN_RUM;
    const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID_RUM;
    if (rumEnabled && clientToken && applicationId && !isCypress()) {
      if (isInitialized.current) return;
      isInitialized.current = true;
      // @see: https://docs.datadoghq.com/real_user_monitoring/browser/setup/client?tab=rum#initialization-parameters
      ddRum.init({
        ...commonDataDogConfig,
        applicationId,
        clientToken,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0,
        trackUserInteractions: true,
        trackViewsManually: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        telemetrySampleRate: 0,
        enableExperimentalFeatures: ['feature_flags'],
        plugins: [reactPlugin()],
        // @see: https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm
        beforeSend: (event) => {
          if (event.error) {
            const { error } = event as RumErrorEvent;
            // Filter out error that might be caused by link validation/pre-fetch rendering from email clicks
            // These errors come from CefSharp in email scanners (particularly Azure/Outlook)
            // @see: https://github.com/DataDog/browser-sdk/issues/2715
            const regex =
              /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;
            if (regex.test(error.message)) return false;
          }

          return true;
        },
      });
    }
  }, []);

  const loc = useKsLocation();
  useEffect(() => {
    ddRum.startView({
      name: loc.type === 'pattern' ? `${loc.type}-${loc.tab.type}` : loc.type,
      context: {
        ksLocation: loc,
      },
    });
  }, [loc]);
}
